import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, Truck, Users, WarehouseIcon, FileBarChart, Database } from 'lucide-react';
import { useAuth } from '../hooks/useSupabase';
import BackupWarning from './BackupWarning';
import supabase from '../services/supabase';

const Layout = ({ children, activeTab, setActiveTab, onUserClick, onLogout }) => {
  const { user } = useAuth();
  // Prendi la versione dell'app dalla variabile d'ambiente
  const appVersion = process.env.REACT_APP_VERSION || '1.0';
  const [showBackupWarning, setShowBackupWarning] = useState(false);

  // Controlla l'ultimo backup usando useCallback
  const checkLastBackup = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('backup_log')
        .select('backup_date')
        .order('backup_date', { ascending: false })
        .limit(1);

      if (error) throw error;

      // Modifica: se non ci sono backup, mostra l'avviso
      if (data.length === 0) {
        setShowBackupWarning(true);
        return;
      }

      // Controlla se l'ultimo backup è stato fatto più di 7 giorni fa
      const lastBackupDate = new Date(data[0].backup_date);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      setShowBackupWarning(lastBackupDate < sevenDaysAgo);
    } catch (err) {
      console.error('Errore nel verificare l\'ultimo backup:', err);
    }
  }, [user]);

  // Aggiorna l'attività quando il componente cambia
  useEffect(() => {
    checkLastBackup();

    // Aggiungi listener per l'evento di backup
    const handleBackupPerformed = () => {
      checkLastBackup();
    };

    window.addEventListener('backup-performed', handleBackupPerformed);

    // Cleanup del listener
    return () => {
      window.removeEventListener('backup-performed', handleBackupPerformed);
    };
  }, [activeTab, checkLastBackup]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Mostra l'avviso di backup se necessario */}
      {showBackupWarning && <BackupWarning onNavigateBackup={() => setActiveTab('backup')} />}

      <header className="bg-blue-600 text-white shadow z-10">
        {/* Modificato il container per rimuovere il padding e il margine a sinistra */}
        <div className="w-full flex justify-between items-center">
          <h1 className="text-xl font-bold p-4">Truck Transport Management</h1>
          
          {user && (
            <div className="flex items-center p-4">
              <button 
                onClick={onUserClick}
                className="mr-4 hover:underline cursor-pointer"
                title="Cambia password"
              >
                {user.email}
              </button>
              <button 
                onClick={onLogout}
                className="p-2 text-white hover:bg-blue-700 rounded-full"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          )}
        </div>
      </header>
      
      <div className="flex flex-1">
        {/* Menu di navigazione laterale */}
        {user && (
          <aside className="w-64 bg-white shadow-md">
            <nav className="mt-5 px-2">
              <button 
                className={`w-full mt-1 flex items-center px-4 py-3 rounded-lg ${activeTab === 'viaggi' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => setActiveTab('viaggi')}
              >
                <Truck size={20} className="mr-3" />
                <span className="font-medium">Gestione Viaggi</span>
              </button>
              
              <button 
                className={`w-full mt-1 flex items-center px-4 py-3 rounded-lg ${activeTab === 'contatti' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => setActiveTab('contatti')}
              >
                <Users size={20} className="mr-3" />
                <span className="font-medium">Anagrafica Contatti</span>
              </button>
              
              <button 
                className={`w-full mt-1 flex items-center px-4 py-3 rounded-lg ${activeTab === 'magazzini' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => setActiveTab('magazzini')}
              >
                <WarehouseIcon size={20} className="mr-3" />
                <span className="font-medium">Anagrafica Magazzini</span>
              </button>
              
              <button 
                className={`w-full mt-1 flex items-center px-4 py-3 rounded-lg ${activeTab === 'report' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => setActiveTab('report')}
              >
                <FileBarChart size={20} className="mr-3" />
                <span className="font-medium">Report</span>
              </button>
              
              <button 
                className={`w-full mt-1 flex items-center px-4 py-3 rounded-lg ${activeTab === 'backup' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => setActiveTab('backup')}
              >
                <Database size={20} className="mr-3" />
                <span className="font-medium">Backup Database</span>
              </button>
            </nav>
          </aside>
        )}
        
        {/* Contenuto principale */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
      
      <footer className="py-4 border-t bg-white">
        <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
          &copy; {new Date().getFullYear()} Truck Transport Management v{appVersion} - 
          <a 
            href="https://www.schwibbert.net" 
            target="_blank" 
            rel="noopener noreferrer"
            className="ml-1 text-blue-600 hover:underline"
          >
            Andrea Schwibbert
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Layout;